// hooks/useChatScroll.js
import { useEffect, useLayoutEffect, useRef } from "react";

const NEAR_BOTTOM_PX = 120;

const idOf = (m) => m?.id ?? m?.messageId ?? m?.raw?.key?.msgId ?? null;

export function useChatScroll({
  chatBodyRef,
  messages,
  chatKey,
  groupTyping,
  typingUsers,
  myChatId,
}) {
  const lastChatKeyRef = useRef(null);
  const lastTailIdRef = useRef(null);
  // Distance from the bottom, updated ONLY by real scroll events. Layout
  // changes (new message, keyboard, reply bar) don't fire scroll, so this
  // always holds the position from BEFORE the change.
  const distRef = useRef(0);
   const lastHeightRef = useRef(0);
  const snapTimerRef = useRef(null);

  const scrollToBottom = (behavior = "auto") => {
    const el = chatBodyRef?.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior });
    });
  };

  // Keyboard / reply bar / growing input resize the list area: keep the same
  // message sitting right above the input (distance 0 = stay pinned to bottom).
  useEffect(() => {
    const el = chatBodyRef?.current;
    if (!el) return;

    const onScroll = () => {
      distRef.current = Math.max(0, el.scrollHeight - el.scrollTop - el.clientHeight);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    lastHeightRef.current = el.clientHeight;

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        const h = el.clientHeight;
        if (!h) return; // display:none background chat
        if (lastHeightRef.current && h !== lastHeightRef.current) {
          el.scrollTop = el.scrollHeight - h - distRef.current;
        }
        lastHeightRef.current = h;
      });
      ro.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro?.disconnect();
    };
  }, [chatBodyRef, chatKey]);

  useLayoutEffect(() => {
    const el = chatBodyRef?.current;
    if (!el) return;

    const last = messages?.[messages.length - 1];
    const tailId = idOf(last);

    const chatChanged = lastChatKeyRef.current !== chatKey;
    lastChatKeyRef.current = chatKey;
    const isNewTail = tailId !== lastTailIdRef.current;
    lastTailIdRef.current = tailId;

    // Landing on a freshly opened chat is owned by MessageList.
    if (chatChanged) return;
    if (!el.clientHeight) return; // hidden chat

    const mine =
      !!last &&
      (last.fromMe === true ||
        last._optimistic === true ||
        (myChatId != null && String(last.senderChatId) === String(myChatId)));

    // Keyboard open = visible area is tiny, so be more forgiving about "near bottom".
    const tol = document.body.classList.contains("keyboard-open")
      ? Math.max(NEAR_BOTTOM_PX, el.clientHeight * 0.5)
      : NEAR_BOTTOM_PX;
    const wasNearBottom = distRef.current <= tol;
    // Outgoing (incl. replies) always snaps. While the keyboard is open,
    // any NEW message (incoming too) snaps so it sits above the keyboard.
    // Otherwise only snap if the user was already near the bottom.
    const keyboardOpen = document.body.classList.contains("keyboard-open");

    if (
      !((mine && isNewTail) || (keyboardOpen && isNewTail) || wasNearBottom)
    ) {
      return;
    }

    const snap = () => {
      if (!el.clientHeight) return;

      el.scrollTop = el.scrollHeight;

      // Programmatic scrolls don't update distRef until the scroll event
      // fires. Without this the ResizeObserver above restores the OLD
      // distance and drags the message back under the keyboard.
      distRef.current = 0;
    };

    // Snap immediately.
    snap();

    // Re-snap after the DOM has settled.
    requestAnimationFrame(snap);

    // Input / reply bar / keyboard heights can settle a frame or two later.
    clearTimeout(snapTimerRef.current);
    snapTimerRef.current = setTimeout(snap, 150);

    // IMPORTANT:
    // Cancel the delayed snap when this effect is replaced or unmounted.
    // This prevents an old chat's timer from fighting the newly active chat.
    return () => {
      clearTimeout(snapTimerRef.current);
      snapTimerRef.current = null;
    };
   
  }, [messages, chatKey, groupTyping, typingUsers, myChatId, chatBodyRef]);

  return { scrollToBottom };
}